/**
 * Direct Orca Whirlpool Swap
 * Bypasses aggregators to swap directly against a Whirlpool
 */

import {
    Connection,
    PublicKey,
    Transaction,
    TransactionInstruction,
    ComputeBudgetProgram,
} from '@solana/web3.js';
import {
    TOKEN_PROGRAM_ID,
    NATIVE_MINT,
    getAssociatedTokenAddress,
    createAssociatedTokenAccountInstruction,
    createSyncNativeInstruction,
} from '@solana/spl-token';
import BN from 'bn.js';

// Orca Whirlpool Program
const WHIRLPOOL_PROGRAM_ID = new PublicKey('whirLbMiicVdio4qvUfM5KAg6Ct8VwpYzGff3uctyCc');

// Tick array size
const TICK_ARRAY_SIZE = 88;

/**
 * Retry wrapper for RPC calls with exponential backoff
 */
async function withRetry<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
): Promise<T> {
    let lastError: Error | null = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error: any) {
            lastError = error;
            // Check if it's a rate limit error
            if (error.message?.includes('429') || error.message?.includes('rate limit')) {
                const delay = baseDelay * Math.pow(2, attempt);
                console.log(`Rate limited, retrying in ${delay}ms...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                throw error; // Non-rate-limit error, throw immediately
            }
        }
    }
    throw lastError;
}

/**
 * Derive Tick Array PDA
 */
function deriveTickArray(whirlpool: PublicKey, startTickIndex: number): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [
            Buffer.from('tick_array'),
            whirlpool.toBuffer(),
            Buffer.from(startTickIndex.toString()),
        ],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

/**
 * Get tick array start index
 */
function getTickArrayStartIndex(tickIndex: number, tickSpacing: number): number {
    const ticksInArray = TICK_ARRAY_SIZE * tickSpacing;
    return Math.floor(tickIndex / ticksInArray) * ticksInArray;
}

/**
 * Derive Oracle PDA
 */
function deriveOracle(whirlpool: PublicKey): PublicKey {
    const [pda] = PublicKey.findProgramAddressSync(
        [Buffer.from('oracle'), whirlpool.toBuffer()],
        WHIRLPOOL_PROGRAM_ID
    );
    return pda;
}

/**
 * Build Swap instruction
 * 
 * Swaps exactInput of tokenA (SOL) for tokenB (TEST)
 */
function buildSwapIx(
    whirlpool: PublicKey,
    tokenAuthority: PublicKey,
    tokenOwnerAccountA: PublicKey,
    tokenVaultA: PublicKey,
    tokenOwnerAccountB: PublicKey,
    tokenVaultB: PublicKey,
    tickArray0: PublicKey,
    tickArray1: PublicKey,
    tickArray2: PublicKey,
    oracle: PublicKey,
    amount: BN,
    otherAmountThreshold: BN,
    sqrtPriceLimit: BN,
    amountSpecifiedIsInput: boolean,
    aToB: boolean
): TransactionInstruction {
    // Swap discriminator
    const discriminator = Buffer.from([248, 198, 158, 145, 225, 117, 135, 200]);
    
    // Data layout: discriminator(8) + amount(8) + other_amount_threshold(8) + sqrt_price_limit(16) + amount_specified_is_input(1) + a_to_b(1)
    const data = Buffer.alloc(8 + 8 + 8 + 16 + 1 + 1);
    let offset = 0;
    
    discriminator.copy(data, offset);
    offset += 8;
    
    // amount (u64)
    amount.toArrayLike(Buffer, 'le', 8).copy(data, offset);
    offset += 8;
    
    // other_amount_threshold (u64)
    otherAmountThreshold.toArrayLike(Buffer, 'le', 8).copy(data, offset);
    offset += 8;
    
    // sqrt_price_limit (u128)
    sqrtPriceLimit.toArrayLike(Buffer, 'le', 16).copy(data, offset);
    offset += 16;
    
    // amount_specified_is_input (bool)
    data.writeUInt8(amountSpecifiedIsInput ? 1 : 0, offset);
    offset += 1;
    
    // a_to_b (bool)
    data.writeUInt8(aToB ? 1 : 0, offset);
    
    return new TransactionInstruction({
        programId: WHIRLPOOL_PROGRAM_ID,
        keys: [
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: tokenAuthority, isSigner: true, isWritable: false },
            { pubkey: whirlpool, isSigner: false, isWritable: true },
            { pubkey: tokenOwnerAccountA, isSigner: false, isWritable: true },
            { pubkey: tokenVaultA, isSigner: false, isWritable: true },
            { pubkey: tokenOwnerAccountB, isSigner: false, isWritable: true },
            { pubkey: tokenVaultB, isSigner: false, isWritable: true },
            { pubkey: tickArray0, isSigner: false, isWritable: true },
            { pubkey: tickArray1, isSigner: false, isWritable: true },
            { pubkey: tickArray2, isSigner: false, isWritable: true },
            { pubkey: oracle, isSigner: false, isWritable: false },
        ],
        data,
    });
}

export interface SwapParams {
    connection: Connection;
    wallet: {
        publicKey: PublicKey;
        signTransaction: (tx: Transaction) => Promise<Transaction>;
        sendTransaction: (tx: Transaction, connection: Connection) => Promise<string>;
    };
    whirlpoolAddress: PublicKey;
    amountIn: bigint;  // Amount of SOL in lamports
    minAmountOut: bigint;  // Minimum tokens to receive (slippage protection)
    onLog?: (message: string) => void;
    // Cached pool data (optional - reduces RPC calls if provided)
    cachedPoolData?: {
        tokenVaultA: string;
        tokenVaultB: string;
        tokenMintA?: string;  // SOL mint
        tokenMintB?: string;  // Token mint
        tickSpacing: number;
    };
}

export interface SwapResult {
    signature: string;
    amountIn: bigint;
    estimatedAmountOut: bigint;
}

/**
 * Execute a direct swap on an Orca Whirlpool
 * Swaps SOL for the token in the pool
 */
export async function swapSolForToken(params: SwapParams): Promise<SwapResult> {
    const { connection, wallet, whirlpoolAddress, amountIn, minAmountOut, cachedPoolData, onLog } = params;
    const log = onLog || console.log;
    
    log("🔄 Starting direct Whirlpool swap...");
    log(`   Pool: ${whirlpoolAddress.toString()}`);
    log(`   Amount In: ${Number(amountIn) / 1e9} SOL`);
    
    let tickSpacing: number;
    let tickCurrentIndex: number;
    let tokenMintA: PublicKey;
    let tokenVaultA: PublicKey;
    let tokenMintB: PublicKey;
    let tokenVaultB: PublicKey;
    
    if (cachedPoolData) {
        // Use cached data - only fetch current tick (minimal RPC call)
        log("   Using cached pool data...");
        tickSpacing = cachedPoolData.tickSpacing;
        tokenVaultA = new PublicKey(cachedPoolData.tokenVaultA);
        tokenVaultB = new PublicKey(cachedPoolData.tokenVaultB);
        tokenMintA = cachedPoolData.tokenMintA ? new PublicKey(cachedPoolData.tokenMintA) : NATIVE_MINT;
        tokenMintB = cachedPoolData.tokenMintB ? new PublicKey(cachedPoolData.tokenMintB) : NATIVE_MINT;
        
        // Still need to fetch current tick (it changes with every trade)
        const whirlpoolAccount = await withRetry(() => connection.getAccountInfo(whirlpoolAddress));
        if (!whirlpoolAccount) {
            throw new Error("Whirlpool not found");
        }
        tickCurrentIndex = whirlpoolAccount.data.readInt32LE(81);
    } else {
        // Fetch full whirlpool data (with retry for rate limits)
        const whirlpoolAccount = await withRetry(() => connection.getAccountInfo(whirlpoolAddress));
        if (!whirlpoolAccount) {
            throw new Error("Whirlpool not found");
        }
        
        const data = whirlpoolAccount.data;
        
        // Parse whirlpool data
        // Layout (with 8-byte Anchor discriminator):
        // - discriminator: 8 bytes (offset 0)
        // - whirlpoolsConfig: 32 bytes (offset 8)
        // - whirlpoolBump: 1 byte (offset 40)
        // - tickSpacing: 2 bytes (offset 41)
        // - tickSpacingSeed: 2 bytes (offset 43)
        // - feeRate: 2 bytes (offset 45)
        // - protocolFeeRate: 2 bytes (offset 47)
        // - liquidity: 16 bytes (offset 49)
        // - sqrtPrice: 16 bytes (offset 65)
        // - tickCurrentIndex: 4 bytes (offset 81)
        // - protocolFeeOwedA: 8 bytes (offset 85)
        // - protocolFeeOwedB: 8 bytes (offset 93)
        // - tokenMintA: 32 bytes (offset 101)
        // - tokenVaultA: 32 bytes (offset 133)
        // - feeGrowthGlobalA: 16 bytes (offset 165)
        // - tokenMintB: 32 bytes (offset 181)
        // - tokenVaultB: 32 bytes (offset 213)
        tickSpacing = data.readUInt16LE(41);
        tickCurrentIndex = data.readInt32LE(81);
        tokenMintA = new PublicKey(data.slice(101, 133));
        tokenVaultA = new PublicKey(data.slice(133, 165));
        tokenMintB = new PublicKey(data.slice(181, 213));
        tokenVaultB = new PublicKey(data.slice(213, 245));
    }
    
    log(`   Token A (SOL): ${tokenMintA.toString().slice(0, 8)}...`);
    log(`   Token B: ${tokenMintB.toString().slice(0, 8)}...`);
    log(`   Current Tick: ${tickCurrentIndex}`);
    log(`   Tick Spacing: ${tickSpacing}`);
    
    // 2. Get/create token accounts
    const tokenAccountA = await getAssociatedTokenAddress(tokenMintA, wallet.publicKey);  // WSOL
    const tokenAccountB = await getAssociatedTokenAddress(tokenMintB, wallet.publicKey);  // Token
    
    // 3. Build transaction
    const tx = new Transaction();
    
    tx.add(
        ComputeBudgetProgram.setComputeUnitLimit({ units: 300000 }),
        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 50000 })
    );
    
    // Create WSOL account if needed (with retry)
    const wsolAccount = await withRetry(() => connection.getAccountInfo(tokenAccountA));
    if (!wsolAccount) {
        log("   Creating WSOL account...");
        tx.add(createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            tokenAccountA,
            wallet.publicKey,
            NATIVE_MINT
        ));
    }
    
    // Create token account if needed (with retry)
    const tokenAccount = await withRetry(() => connection.getAccountInfo(tokenAccountB));
    if (!tokenAccount) {
        log("   Creating token account...");
        tx.add(createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            tokenAccountB,
            wallet.publicKey,
            tokenMintB
        ));
    }
    
    // Transfer SOL to WSOL account and sync
    const { SystemProgram } = await import('@solana/web3.js');
    tx.add(
        SystemProgram.transfer({
            fromPubkey: wallet.publicKey,
            toPubkey: tokenAccountA,
            lamports: amountIn,
        }),
        createSyncNativeInstruction(tokenAccountA)
    );
    
    // 4. Calculate tick arrays needed for swap
    // For a swap going A to B (SOL to Token), we need tick arrays going DOWN
    const tickArrayStart0 = getTickArrayStartIndex(tickCurrentIndex, tickSpacing);
    const tickArrayStart1 = tickArrayStart0 - (TICK_ARRAY_SIZE * tickSpacing);
    const tickArrayStart2 = tickArrayStart1 - (TICK_ARRAY_SIZE * tickSpacing);
    
    const tickArray0 = deriveTickArray(whirlpoolAddress, tickArrayStart0);
    const tickArray1 = deriveTickArray(whirlpoolAddress, tickArrayStart1);
    const tickArray2 = deriveTickArray(whirlpoolAddress, tickArrayStart2);
    
    log(`   Tick Arrays: ${tickArrayStart0}, ${tickArrayStart1}, ${tickArrayStart2}`);
    
    // 5. Derive oracle
    const oracle = deriveOracle(whirlpoolAddress);
    
    // 6. Build swap instruction
    // For A to B swap (SOL -> Token):
    // - amount = SOL amount in
    // - sqrtPriceLimit = minimum sqrt price (we want price to go down, so set to MIN)
    const MIN_SQRT_PRICE = new BN("4295048016");  // Minimum sqrt price
    
    log("   Building swap instruction...");
    tx.add(buildSwapIx(
        whirlpoolAddress,
        wallet.publicKey,  // tokenAuthority
        tokenAccountA,     // tokenOwnerAccountA (WSOL)
        tokenVaultA,       // tokenVaultA
        tokenAccountB,     // tokenOwnerAccountB (Token)
        tokenVaultB,       // tokenVaultB
        tickArray0,
        tickArray1,
        tickArray2,
        oracle,
        new BN(amountIn.toString()),           // amount
        new BN(minAmountOut.toString()),       // other_amount_threshold (min output)
        MIN_SQRT_PRICE,                        // sqrt_price_limit
        true,                                  // amount_specified_is_input
        true                                   // a_to_b (SOL -> Token)
    ));
    
    // 7. Send transaction
    log("⏳ Sending swap transaction...");
    tx.feePayer = wallet.publicKey;
    const blockhash = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash.blockhash;
    
    const signature = await wallet.sendTransaction(tx, connection);
    log(`✅ Swap sent: ${signature}`);
    
    // Confirm
    await connection.confirmTransaction({
        signature,
        blockhash: blockhash.blockhash,
        lastValidBlockHeight: blockhash.lastValidBlockHeight,
    }, 'confirmed');
    
    log("🎉 Swap confirmed!");
    
    // Estimate output (rough calculation)
    const estimatedAmountOut = amountIn * BigInt(40_000_000);  // ~40M tokens per SOL
    
    return {
        signature,
        amountIn,
        estimatedAmountOut,
    };
}

/**
 * Execute a direct swap on an Orca Whirlpool
 * Swaps Token for SOL (sell)
 */
export async function swapTokenForSol(params: SwapParams & { tokenMint: PublicKey }): Promise<SwapResult> {
    const { connection, wallet, whirlpoolAddress, amountIn, minAmountOut, tokenMint, cachedPoolData, onLog } = params;
    const log = onLog || console.log;
    
    log("🔄 Starting direct Whirlpool swap (SELL)...");
    log(`   Pool: ${whirlpoolAddress.toString()}`);
    log(`   Amount In: ${Number(amountIn) / 1e9} tokens`);
    
    let tickSpacing: number;
    let tickCurrentIndex: number;
    let tokenMintA: PublicKey;
    let tokenVaultA: PublicKey;
    let tokenMintB: PublicKey;
    let tokenVaultB: PublicKey;
    
    if (cachedPoolData) {
        // Use cached data - only fetch current tick (minimal RPC call)
        log("   Using cached pool data...");
        tickSpacing = cachedPoolData.tickSpacing;
        tokenVaultA = new PublicKey(cachedPoolData.tokenVaultA);
        tokenVaultB = new PublicKey(cachedPoolData.tokenVaultB);
        tokenMintA = cachedPoolData.tokenMintA ? new PublicKey(cachedPoolData.tokenMintA) : NATIVE_MINT;
        tokenMintB = cachedPoolData.tokenMintB ? new PublicKey(cachedPoolData.tokenMintB) : tokenMint;
        
        // Still need to fetch current tick (it changes with every trade)
        const whirlpoolAccount = await withRetry(() => connection.getAccountInfo(whirlpoolAddress));
        if (!whirlpoolAccount) {
            throw new Error("Whirlpool not found");
        }
        tickCurrentIndex = whirlpoolAccount.data.readInt32LE(81);
    } else {
        // Fetch full whirlpool data (with retry for rate limits)
        const whirlpoolAccount = await withRetry(() => connection.getAccountInfo(whirlpoolAddress));
        if (!whirlpoolAccount) {
            throw new Error("Whirlpool not found");
        }
        
        const data = whirlpoolAccount.data;
        
        // Parse whirlpool data (same layout as swapSolForToken)
        tickSpacing = data.readUInt16LE(41);
        tickCurrentIndex = data.readInt32LE(81);
        tokenMintA = new PublicKey(data.slice(101, 133));
        tokenVaultA = new PublicKey(data.slice(133, 165));
        tokenMintB = new PublicKey(data.slice(181, 213));
        tokenVaultB = new PublicKey(data.slice(213, 245));
    }
    
    log(`   Token A (SOL): ${tokenMintA.toString().slice(0, 8)}...`);
    log(`   Token B: ${tokenMintB.toString().slice(0, 8)}...`);
    log(`   Current Tick: ${tickCurrentIndex}`);
    log(`   Tick Spacing: ${tickSpacing}`);
    
    // Verify that tokenMint matches tokenMintB (the token we're selling)
    if (!tokenMintB.equals(tokenMint)) {
        throw new Error(`Token mint mismatch: expected ${tokenMintB.toString()}, got ${tokenMint.toString()}`);
    }
    
    // 2. Get/create token accounts
    const tokenAccountA = await getAssociatedTokenAddress(tokenMintA, wallet.publicKey);  // WSOL (receiving)
    const tokenAccountB = await getAssociatedTokenAddress(tokenMintB, wallet.publicKey);  // Token (sending)
    
    // 3. Build transaction
    const tx = new Transaction();
    
    tx.add(
        ComputeBudgetProgram.setComputeUnitLimit({ units: 300000 }),
        ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 50000 })
    );
    
    // Create WSOL account if needed (to receive SOL) - with retry
    const wsolAccount = await withRetry(() => connection.getAccountInfo(tokenAccountA));
    const wsolAccountExisted = !!wsolAccount;
    if (!wsolAccount) {
        log("   Creating WSOL account...");
        tx.add(createAssociatedTokenAccountInstruction(
            wallet.publicKey,
            tokenAccountA,
            wallet.publicKey,
            NATIVE_MINT
        ));
    }
    
    // Token account must already exist (user is selling their tokens) - with retry
    const tokenAccount = await withRetry(() => connection.getAccountInfo(tokenAccountB));
    if (!tokenAccount) {
        throw new Error("Token account not found - you don't have any tokens to sell");
    }
    
    // Verify the account has enough balance
    // Parse token account to check balance
    try {
        const parsedAccount = await connection.getParsedAccountInfo(tokenAccountB);
        if (parsedAccount.value) {
            const parsedInfo = (parsedAccount.value.data as any).parsed?.info;
            const tokenAmount = parsedInfo?.tokenAmount;
            if (tokenAmount) {
                const balance = BigInt(tokenAmount.amount || '0');
                if (balance < amountIn) {
                    throw new Error(`Insufficient token balance. You have ${Number(balance) / Math.pow(10, tokenAmount.decimals || 9)} tokens, trying to sell ${Number(amountIn) / Math.pow(10, tokenAmount.decimals || 9)}`);
                }
                log(`   Token balance verified: ${tokenAmount.uiAmount} tokens`);
            }
        }
    } catch (balanceError: any) {
        if (balanceError.message?.includes('Insufficient')) {
            throw balanceError;
        }
        log(`   Warning: Could not verify token balance: ${balanceError.message}`);
    }
    
    // 4. Calculate tick arrays needed for swap
    // For B->A swaps, use the SAME calculation as A->B (going DOWN)
    // Whirlpool handles the direction internally via the a_to_b flag
    // The tick arrays just need to be sequential and cover the swap range
    const tickArrayStart0 = getTickArrayStartIndex(tickCurrentIndex, tickSpacing);
    const tickArrayStart1 = tickArrayStart0 - (TICK_ARRAY_SIZE * tickSpacing);
    const tickArrayStart2 = tickArrayStart1 - (TICK_ARRAY_SIZE * tickSpacing);
    
    log(`   Current tick: ${tickCurrentIndex}, Tick array starts: ${tickArrayStart0}, ${tickArrayStart1}, ${tickArrayStart2}`);
    
    const tickArray0 = deriveTickArray(whirlpoolAddress, tickArrayStart0);
    const tickArray1 = deriveTickArray(whirlpoolAddress, tickArrayStart1);
    const tickArray2 = deriveTickArray(whirlpoolAddress, tickArrayStart2);
    
    log(`   Tick Arrays (same as A->B, going DOWN): ${tickArrayStart0}, ${tickArrayStart1}, ${tickArrayStart2}`);
    
    // 5. Derive oracle
    const oracle = deriveOracle(whirlpoolAddress);
    
    // 6. Build swap instruction
    // For B to A swap (Token -> SOL):
    // - amount = Token amount in
    // - sqrtPriceLimit = maximum sqrt price (we want price to go up, so set to MAX)
    const MAX_SQRT_PRICE = new BN("79226673515401279992447579055");  // Maximum sqrt price
    
    log("   Building swap instruction...");
    // For B->A swaps, use the SAME tick array order as A->B
    // The a_to_b flag handles the direction internally
    tx.add(buildSwapIx(
        whirlpoolAddress,
        wallet.publicKey,  // tokenAuthority
        tokenAccountA,     // tokenOwnerAccountA (WSOL - receiving)
        tokenVaultA,       // tokenVaultA
        tokenAccountB,     // tokenOwnerAccountB (Token - sending)
        tokenVaultB,       // tokenVaultB
        tickArray0,        // Same order as A->B
        tickArray1,
        tickArray2,
        oracle,
        new BN(amountIn.toString()),           // amount
        new BN(minAmountOut.toString()),       // other_amount_threshold (min SOL output)
        MAX_SQRT_PRICE,                        // sqrt_price_limit
        true,                                  // amount_specified_is_input
        false                                  // a_to_b = false (B to A, Token -> SOL)
    ));
    
    // Close WSOL account to unwrap SOL back to native SOL
    // Only close if account existed before to avoid Phantom warnings about closing newly created accounts
    // If account was just created, it will remain open with WSOL - user can close manually or we handle in follow-up
    if (wsolAccountExisted) {
        const { createCloseAccountInstruction } = await import('@solana/spl-token');
        tx.add(createCloseAccountInstruction(
            tokenAccountA,
            wallet.publicKey,  // destination (receives the SOL)
            wallet.publicKey   // owner
        ));
        log("   Added close WSOL account instruction to unwrap SOL (account existed)");
    } else {
        log("   WSOL account was just created - leaving open to avoid transaction warnings");
        log("   Note: User will receive wrapped SOL that can be unwrapped manually");
    }
    
    // 7. Send transaction
    log("⏳ Sending swap transaction...");
    tx.feePayer = wallet.publicKey;
    const blockhash = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash.blockhash;
    
    const signature = await wallet.sendTransaction(tx, connection);
    log(`✅ Swap sent: ${signature}`);
    
    // Confirm
    await connection.confirmTransaction({
        signature,
        blockhash: blockhash.blockhash,
        lastValidBlockHeight: blockhash.lastValidBlockHeight,
    }, 'confirmed');
    
    log("🎉 Swap confirmed!");
    
    // Estimate output (rough calculation)
    const estimatedAmountOut = amountIn / BigInt(40_000_000);  // ~40M tokens per SOL
    
    return {
        signature,
        amountIn,
        estimatedAmountOut,
    };
}

export default swapSolForToken;

